import { useEffect, useState } from "react";
import { useApi } from "../../lib/useApi";
import { fechaCorta } from "../../lib/fecha-ui";
import { Card, Chip, TableHead, TableRow, Th, Td, EmptyState } from "../../components/ui";
import type { SesionActiva } from "../../lib/tipos";

// Faltantes de MI botica (§8): stock<=mínimo O quiebres en 14 días. La ruta enriquece cada fila con
// `origen` ('oido'|'manual'|null) y, para los de oído, la `frase` que lo disparó (mono, no acusatoria).
type Origen = "oido" | "manual" | null;
type Fila = { producto_id: string; nombre: string; stock: number; minimo: number; quiebres_14d: number; sugerido: number; origen: Origen; frase?: string | null };

// "Cómo se supo": el oído del mostrador (rojo), reportado a mano, o solo por debajo del mínimo.
function comoSeSupo(origen: Origen) {
  if (origen === "oido") return <Chip variant="danger">El oído</Chip>;
  if (origen === "manual") return <Chip variant="neutral">Registrado a mano</Chip>;
  return <Chip variant="neutral">Bajo mínimo</Chip>;
}

export function Faltantes({ sesion, onCount, sucursalId }: { sesion: SesionActiva; onCount?: (n: number) => void; sucursalId?: string }) {
  // El Dueño elige la botica arriba; sin botica elegida no hay «uno por uno» que mostrar.
  const puedeVerUnoPorUno = sesion.usuario.rol !== "super_admin" || !!sucursalId;
  const { data, cargando, error, recargar } = useApi<{ faltantes: Fila[] }>(sucursalId ? `/faltantes?sucursal_id=${sucursalId}` : "/faltantes", [sucursalId]);
  useEffect(() => {
    if (data) onCount?.(data.faltantes.length);
  }, [data, onCount]);

  const cols = "2fr 200px 200px";

  return (
    <Card className="gap-3">
      <p className="text-[12.5px] text-ink-2">Lo que se acabó o se pidió y no había. Viene del oído del mostrador o de lo que se anota a mano.</p>
      {cargando ? (
        <p className="p-6 text-center text-[13px] text-ink-3">Cargando faltantes…</p>
      ) : error ? (
        <div className="p-6 text-center">
          <p className="text-[13px] text-accent-ink">{error}</p>
          <button onClick={recargar} className="mt-2 text-[12.5px] text-link underline">Reintentar</button>
        </div>
      ) : (data?.faltantes.length ?? 0) === 0 ? (
        <EmptyState title="Sin productos faltantes" subtitle="Ningún producto del catálogo está bajo su mínimo. Lo anotado a mano sin producto está abajo, en «uno por uno»." />
      ) : (
        <div>
          <TableHead cols={cols}>
            <Th>Producto</Th>
            <Th>Cómo se supo</Th>
            <Th>Acción</Th>
          </TableHead>
          {data!.faltantes.map((f) => (
            <TableRow key={f.producto_id} cols={cols}>
              <Td className="min-w-0">
                <p className="truncate text-[13px] font-semibold text-ink">{f.nombre}</p>
                {f.origen === "oido" && f.frase && <p className="truncate font-mono text-[11.5px] text-ink-3-alt">"{f.frase}"</p>}
              </Td>
              <Td>{comoSeSupo(f.origen)}</Td>
              <Td>
                <span className="inline-flex items-center gap-1.5 text-[12px] text-ink-3">
                  Pasar a pedido
                  <Chip variant="neutral" className="opacity-70">próximamente</Chip>
                </span>
              </Td>
            </TableRow>
          ))}
        </div>
      )}
      {puedeVerUnoPorUno && <UnoPorUno sucursalId={sucursalId} />}
    </Card>
  );
}

// «Uno por uno» (C-6, tramo 07 de huayruro-orden-y-mapa): cada faltante que se anotó en el mostrador, con
// su fecha, en los últimos 30 días. La tabla de arriba los junta por producto; aquí se ve cada vez que
// alguien pidió algo que no había (incluidos los que no están en el catálogo). Carga solo al abrirse.
type Anotado = { id: string; nombre: string | null; gtin_consultado: string | null; descripcion_libre: string | null; fecha_hora: string };
function UnoPorUno({ sucursalId }: { sucursalId?: string | undefined }) {
  const [abierto, setAbierto] = useState(false);
  const q = sucursalId ? `?sucursal_id=${sucursalId}` : "";
  const lista = useApi<{ quiebres: Anotado[] }>(abierto ? `/quiebres${q}` : null, [abierto, sucursalId]);
  const filas = lista.data?.quiebres ?? [];
  return (
    <details className="group" onToggle={(e) => setAbierto((e.target as HTMLDetailsElement).open)}>
      <summary className="flex min-h-[44px] cursor-pointer list-none items-center text-[12.5px] font-semibold text-link hover:text-link-hover">
        Ver uno por uno lo que se anotó (últimos 30 días) ▾
      </summary>
      {lista.cargando || (abierto && !lista.data && !lista.error) ? (
        <p className="py-2 text-[13px] text-ink-3">Cargando…</p>
      ) : lista.error ? (
        <p className="py-2 text-[12.5px] text-accent-ink">{lista.error}</p>
      ) : filas.length === 0 ? (
        <p className="py-2 text-[12.5px] text-ink-3">No se anotó ningún faltante en los últimos 30 días.</p>
      ) : (
        <ul className="divide-y divide-line-row">
          {filas.map((f) => (
            <li key={f.id} className="flex items-baseline justify-between gap-3 py-1.5">
              <span className="min-w-0 text-[12.5px] text-ink">
                {f.nombre ?? f.descripcion_libre ?? (f.gtin_consultado ? `Código ${f.gtin_consultado}` : "Sin descripción")}
                {f.nombre && f.descripcion_libre && f.descripcion_libre !== f.nombre ? <span className="text-ink-2"> · {f.descripcion_libre}</span> : null}
              </span>
              <span className="shrink-0 text-[12px] text-ink-2">{fechaCorta(f.fecha_hora)}</span>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
